import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Container } from "@/components/atoms";
import { useAppDispatch } from "@/redux/hooks";
import { clearBooking, initBookingScope, initialState, restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { buildRebookDraft, RebookSource } from "@/redux/slices/clientBooking/rebookDraft";
import { ConfirmationSuccessHeader } from "./booking/confirmation/ConfirmationSuccessHeader";
import { MobileAppPromo } from "./booking/confirmation/MobileAppPromo";
import { PriceSummary, PriceSummaryRow } from "./booking/confirmation/PriceSummary";
import { TripDetails, TripDetailsCard } from "./booking/confirmation/TripDetailsCard";
import { useClientScope } from "./settings/ClientScopeContext";

/**
 * Serialisable snapshot handed to the confirmation via router state (PricedBookingSummary.submit),
 * captured just before the draft is cleared — everything the confirmation renders, no store reads.
 * A hard refresh drops router state and the page degrades to the success header + promo only.
 */
export interface ClientBookingConfirmationState {
  /** The created booking's display number ("J123456") — names the success heading. */
  displayJobId?: string;
  /**
   * Where the operator started the OBO booking, threaded from the entry point so the confirmation's
   * "Back" link returns them there. `"client-profile"` (booked from a client's profile / the clients
   * table row action) → "Back to Client Profile"; absent (booked from the Booking Manager) → "Back to
   * Bookings". Ignored for self-service clients ("Back to Home").
   */
  bookingOrigin?: "client-profile";
  trip: TripDetails;
  /** Raw trip facts for a re-book (reverse/duplicate) — the display `trip` has formatted strings only. */
  rebook?: RebookSource;
  /** Quote price rows from the shared QuotePriceSummary mapping — matches the Payment step exactly. */
  priceGroups?: PriceSummaryRow[][];
  total?: string;
}

/**
 * Booking confirmation — the final step of the client booking flow. Renders the same three boxes
 * as the Booking Summary panel (Address / Trip Details / Payment) plus the priced summary, from a
 * snapshot passed in router state.
 *
 * lg+:    gray surface + a bordered two-column white card (details + price); inline actions in the card,
 *         "Back to Home" below it; promo is two columns.
 * < lg:   whole page is white (no card chrome); details + price stack directly on the page; the actions
 *         live in a fixed bottom bar — side-by-side on tablet, stacked on mobile.
 */
export const ClientBookingConfirmation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const snapshot = (state as ClientBookingConfirmationState | null) ?? null;
  const { accountId, clientName } = useClientScope();
  const { operatorId, accessId } = useParams<{ operatorId: string; accessId: string }>();
  const isOperatorScoped = !!accountId && !!operatorId;
  // EA booked on behalf of a managed client (DEV-1296) — the scoped route carries `:accessId`.
  const isAssistantScoped = !!accountId && !!accessId;

  const bookingPath = isOperatorScoped
    ? `/operator/${operatorId}/clients/${accountId}/booking`
    : isAssistantScoped
    ? `/client/book-for/${accessId}`
    : "/client/booking";
  // Operator OBO started from a client's profile (or the clients table) returns to that client's profile;
  // started from the Booking Manager returns to the bookings list. EA managed-booking returns to My
  // Clients; self-service returns home.
  const fromClientProfile = isOperatorScoped && snapshot?.bookingOrigin === "client-profile";
  const homePath = fromClientProfile
    ? `/operator/${operatorId}/clients/${accountId}/details`
    : isOperatorScoped
    ? `/operator/${operatorId}/bookings`
    : isAssistantScoped
    ? "/client/my-clients"
    : "/client";
  const goHome = () => navigate(homePath);
  const homeLabel = fromClientProfile
    ? "Back to Client Profile"
    : isOperatorScoped
    ? "Back to Bookings"
    : isAssistantScoped
    ? "Back to My Clients"
    : "Back to Home";

  /**
   * Seed the next booking from this finished one and open the sheet. initBookingScope BEFORE the
   * restore aligns the draft's scope to the current account, so ClientBooking's own scope guard on
   * mount is a no-op and can't wipe the seeded draft (matters under operator OBO).
   */
  const rebook = (mode: "return" | "fresh") => {
    dispatch(initBookingScope(accountId ?? null));
    // "fresh" needs no source; "return" reverses the finished trip via its raw facts.
    const draft = mode === "return" && snapshot?.rebook ? buildRebookDraft(snapshot.rebook, "return") : {};
    dispatch(mode === "return" && snapshot?.rebook ? restoreBookingDraft({ ...initialState, ...draft }) : clearBooking());
    navigate(bookingPath);
  };

  const bookReturnTrip = () => rebook("return");
  const bookInterstateTrip = () => rebook("fresh");

  return (
    <div className="flex flex-1 flex-col bg-white lg:bg-transparent">
      {/* Confirmation + trip details (card chrome only on lg+) */}
      <div className="lg:bg-neutral-surface-gray">
        {/* Page padding: 60px top + sides on tablet, 20px sides on mobile; desktop keeps its own. */}
        <Container className="flex flex-col items-center px-5 pt-[60px] md:px-[60px] lg:px-4 lg:pb-[18px] lg:pt-14">
          <ConfirmationSuccessHeader displayJobId={snapshot?.displayJobId} clientName={clientName} />

          {snapshot && (
            <>
              <div className="mt-6 w-full max-w-[1000px] lg:rounded-2xl lg:border lg:border-neutral-mid-gray lg:bg-white lg:p-[30px] lg:shadow-sm">
                <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
                  <div className="flex-1">
                    <TripDetailsCard trip={snapshot.trip} />
                  </div>
                  {/* Only when the snapshot carries a real price — never the layout-only sample. */}
                  {snapshot.priceGroups && snapshot.total && (
                    <div className="lg:w-[320px] lg:shrink-0">
                      <PriceSummary groups={snapshot.priceGroups} total={snapshot.total} />
                    </div>
                  )}
                </div>

                {/* Inline actions — desktop only (tablet/mobile use the fixed bottom bar) */}
                <div className="mt-8 hidden justify-center gap-3 lg:flex">
                  <Button variant="secondary" size="md" onClick={bookInterstateTrip}>
                    Book an Interstate Trip
                  </Button>
                  <Button variant="primary" size="md" onClick={bookReturnTrip}>
                    Book a Return Trip
                  </Button>
                </div>
              </div>

              {/* Back to Home — desktop only */}
              <Button variant="custom" size="lg" onClick={goHome} className="mt-5 hidden text-info lg:inline-flex">
                {homeLabel}
              </Button>
            </>
          )}
        </Container>
      </div>

      {/* Mobile app promo band — full width; 60/20px sides, 32px bottom on tablet/mobile */}
      <div className="bg-white">
        <Container className="px-5 pb-8 pt-14 md:px-[60px] lg:px-4 lg:pb-14 lg:pt-[65px]">
          <MobileAppPromo />
        </Container>
      </div>

      {/* Fixed bottom action bar — tablet/mobile only */}
      {snapshot && (
        <div className="sticky bottom-0 z-10 border-t border-neutral-mid-gray bg-white p-4 lg:hidden">
          <div className="flex flex-col gap-2">
            {/* Tablet: side-by-side (Interstate left, Return right). Mobile: stacked (Return on top). */}
            <div className="flex flex-col gap-2 md:flex-row-reverse md:items-center md:justify-center md:gap-3">
              <Button variant="primary" size="lg" className="w-full md:w-auto" onClick={bookReturnTrip}>
                Book a Return Trip
              </Button>
              <Button variant="secondary" size="lg" className="w-full md:w-auto" onClick={bookInterstateTrip}>
                Book an Interstate Trip
              </Button>
            </div>
            <Button variant="custom" size="md" onClick={goHome} className="text-info">
              {homeLabel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
